<template>
  <RevCard class="flex-1 p-24">
    <h2 class="heading-2 mb-8">
      {{ i18n(translations.title) }}
    </h2>
    <div class="text-action-default-low body-1 mb-16">
      {{ i18n(translations.description) }}
    </div>
    <WithSkeletonLoader :is-loading="pending">
      <template v-if="legalDetailsMetadata && eprInformation">
        <EprDetailsByMarketList
          v-for="market in eprMarkets"
          :key="market"
          :epr-details="eprInformation[market]"
          :market
          @delete-epr-information="handleDeleteEprInformation"
          @select-market="handleSelectEprMarket"
        />
        <EprDetailsHelp />
        <EprDetailsModal
          v-if="selectedEprMarket"
          :market="selectedEprMarket"
          :market-requirements="
            legalDetailsMetadata?.eprMarketRequirements[selectedEprMarket]
          "
          @update-epr-information="handleEprInformationUpdated"
        />
        <EprDetailDeletionConfirmModal
          v-if="eprDetailToDeleteId"
          :epr-detail-id="eprDetailToDeleteId"
          @update-epr-imformation="handleEprInformationUpdated"
        />
      </template>
    </WithSkeletonLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type {
  GetEprInformationItem,
  GetLegalDetailsMetadata,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES } from '../../../../Profile.constants'
import WithSkeletonLoader from '../../../../components/WithSkeletonLoader/WithSkeletonLoader.vue'

import translations from './EprDetails.translations'
import EprDetailDeletionConfirmModal from './components/EprDetailDeletionConfirmModal/EprDetailDeletionConfirmModal.vue'
import EprDetailsByMarketList from './components/EprDetailsByMarketList/EprDetailsByMarketList.vue'
import EprDetailsHelp from './components/EprDetailsHelp/EprDetailsHelp.vue'
import EprDetailsModal from './components/EprDetailsModal/EprDetailsModal.vue'

const props = defineProps<{
  legalDetailsMetadata: GetLegalDetailsMetadata | null
}>()

const i18n = useI18n()
const { openErrorToast } = useTheToast()

const {
  data: eprInformation,
  error,
  pending,
  refresh,
} = await useHttpLazyFetch(sellerProfileAPI.getEprInformation)

if (error.value) {
  openErrorToast({
    title: i18n(translations.fetchErrorTitle),
    content: i18n(translations.fetchEprInformationErrorMessage),
  })
}

const selectedEprMarket = ref<MarketCountryCode>()
const eprDetailToDeleteId = ref<GetEprInformationItem['id']>()

const eprMarkets = computed<MarketCountryCode[]>(
  () =>
    Object.keys(
      props.legalDetailsMetadata?.eprMarketRequirements || {},
    ) as MarketCountryCode[],
)

function handleSelectEprMarket(market: MarketCountryCode) {
  selectedEprMarket.value = market
  openModal(MODAL_NAMES.EDIT_EPR_DETAILS)
}

function handleDeleteEprInformation(id: GetEprInformationItem['id']) {
  eprDetailToDeleteId.value = id
  openModal(MODAL_NAMES.DELETE_EPR_DETAIL)
}

function handleEprInformationUpdated() {
  refresh()
}
</script>
